import React, { useRef, useEffect, useState } from "react";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Quote, Minus, Eraser } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, imgSrc } from "@/lib/api";

const Btn = ({ title, onClick, children, testId }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testId} className="rounded-md p-1.5 text-[var(--text-muted)] hover:bg-[var(--accent-a10)] hover:text-[var(--accent-gold)]">{children}</button>
);

export const RichEditor = ({ value, onChange, testId = "rich-editor" }) => {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
  }, [value]);

  const exec = (cmd, arg = null) => { document.execCommand(cmd, false, arg); emit(); };
  const emit = () => onChange(ref.current?.innerHTML || "");

  const addLink = () => {
    const url = window.prompt("Link URL (https://… or /properties)");
    if (!url) return;
    exec("createLink", url);
  };

  const insertImage = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      ref.current?.focus();
      exec("insertHTML", `<figure><img src="${imgSrc(data.url)}" alt="" style="max-width:100%;height:auto;border-radius:12px" /></figure><p><br/></p>`);
      toast.success("Image inserted");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)]" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--border-subtle)] bg-[var(--bg-card)] p-1.5">
        <Btn title="Bold" onClick={() => exec("bold")} testId="re-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" onClick={() => exec("italic")} testId="re-italic"><Italic className="h-4 w-4" /></Btn>
        <Btn title="Underline" onClick={() => exec("underline")}><Underline className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-[var(--border-subtle)]" />
        <Btn title="Heading" onClick={() => exec("formatBlock", "<h2>")} testId="re-h2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="Sub-heading" onClick={() => exec("formatBlock", "<h3>")}><Heading3 className="h-4 w-4" /></Btn>
        <Btn title="Paragraph" onClick={() => exec("formatBlock", "<p>")}><span className="px-1 text-xs font-semibold">P</span></Btn>
        <Btn title="Quote" onClick={() => exec("formatBlock", "<blockquote>")}><Quote className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-[var(--border-subtle)]" />
        <Btn title="Bullet list" onClick={() => exec("insertUnorderedList")} testId="re-ul"><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn title="Divider" onClick={() => exec("insertHorizontalRule")}><Minus className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-[var(--border-subtle)]" />
        <Btn title="Insert link" onClick={addLink} testId="re-link"><LinkIcon className="h-4 w-4" /></Btn>
        <Btn title={busy ? "Uploading…" : "Insert image"} onClick={() => fileRef.current?.click()} testId="re-image"><ImageIcon className="h-4 w-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => insertImage(e.target.files?.[0])} data-testid="re-image-file" />
        <Btn title="Clear formatting" onClick={() => exec("removeFormat")}><Eraser className="h-4 w-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid="re-content"
        className="prose-arch min-h-[260px] max-h-[55vh] overflow-y-auto px-4 py-3 text-sm text-[var(--text-primary)] outline-none"
      />
    </div>
  );
};

export default RichEditor;
